import { ReactNode, createContext, useContext } from 'react';

/**
 * Numbering, done by the sheet as the document prints.
 *
 * Sections, subsections, figures and numbered tables take their numbers from
 * the order they render in, so the numbers always follow the printed order: a
 * section left out of the layout takes no number, a figure in it is never
 * counted, and the figure and table indexes list exactly what printed. A
 * report doesn't number anything itself.
 *
 * How: ReportSheet starts a fresh count every time it renders, and each
 * numbered element takes the next number the first time it renders in that
 * pass — React renders a tree in document order. An element that re-renders
 * on its own later (a gallery whose images arrive) finds its number already
 * given. The indexes must render after what they list; they belong at the end.
 */

export interface SheetSection {
  key: string;
  title: string;
  /** "3", or null for an unnumbered section such as an appendix */
  number: string | null;
  anchor: string;
}

export interface SheetFigure {
  key: string;
  number: number;
  anchor: string;
  caption: ReactNode;
  fileId?: string;
  fileName?: string;
  /** the section it printed in */
  section: string | null;
}

export interface SheetTable {
  key: string;
  number: number;
  anchor: string;
  title: ReactNode;
  section: string | null;
}

export class SheetRegistry {
  sections: SheetSection[] = [];
  figures: SheetFigure[] = [];
  tables: SheetTable[] = [];
  private sectionsByKey = new Map<string, SheetSection>();
  private figuresByKey = new Map<string, SheetFigure>();
  private tablesByKey = new Map<string, SheetTable>();
  private subsections = new Map<string, Map<string, number>>();
  private numbered = 0;

  /** Start the count again; ReportSheet calls it at the top of every render. */
  reset(): void {
    this.sections = [];
    this.figures = [];
    this.tables = [];
    this.sectionsByKey.clear();
    this.figuresByKey.clear();
    this.tablesByKey.clear();
    this.subsections.clear();
    this.numbered = 0;
  }

  section(key: string, title: string, numbered: boolean): SheetSection {
    const existing = this.sectionsByKey.get(key);
    if (existing) return existing;
    const entry: SheetSection = { key, title, number: numbered ? String(++this.numbered) : null, anchor: `section-${key}` };
    this.sectionsByKey.set(key, entry);
    this.sections.push(entry);
    return entry;
  }

  subsection(sectionKey: string, key: string): number {
    const inSection = this.subsections.get(sectionKey) ?? new Map<string, number>();
    this.subsections.set(sectionKey, inSection);
    if (!inSection.has(key)) inSection.set(key, inSection.size + 1);
    return inSection.get(key) as number;
  }

  figure(key: string, info: Omit<SheetFigure, 'key' | 'number' | 'anchor'>): SheetFigure {
    const existing = this.figuresByKey.get(key);
    if (existing) return existing;
    const number = this.figures.length + 1;
    const entry: SheetFigure = { ...info, key, number, anchor: `fig-${number}` };
    this.figuresByKey.set(key, entry);
    this.figures.push(entry);
    return entry;
  }

  table(key: string, info: Omit<SheetTable, 'key' | 'number' | 'anchor'>): SheetTable {
    const existing = this.tablesByKey.get(key);
    if (existing) return existing;
    const number = this.tables.length + 1;
    const entry: SheetTable = { ...info, key, number, anchor: `table-${number}` };
    this.tablesByKey.set(key, entry);
    this.tables.push(entry);
    return entry;
  }
}

export const SheetContext = createContext<SheetRegistry | null>(null);
const SectionContext = createContext<SheetSection | null>(null);

/** The sheet numbering this document; null outside one (an inputs panel, a test rendering a block alone). */
export function useSheet(): SheetRegistry | null {
  return useContext(SheetContext);
}

/** The section this element prints in; null outside one. */
export function useSheetSection(): SheetSection | null {
  return useContext(SectionContext);
}

interface SectionProps {
  /** stable within the document: the layout section's id */
  id: string;
  title: string;
  /** false for an appendix: the title prints alone */
  numbered?: boolean;
  /** controls beside the heading */
  actions?: ReactNode;
  children: ReactNode;
}

/** A section of the document, numbered by the sheet in the order sections print. */
export function Section({ id, title, numbered = true, actions, children }: SectionProps) {
  const sheet = useSheet();
  const entry = sheet?.section(id, title, numbered) ?? { key: id, title, number: null, anchor: `section-${id}` };
  return (
    <SectionContext.Provider value={entry}>
      <section className="card" aria-labelledby={`${entry.anchor}-heading`}>
        <h2 id={entry.anchor}>
          <span id={`${entry.anchor}-heading`}>{entry.number ? `${entry.number} · ${title}` : title}</span>
          {actions}
        </h2>
        <div className="card-body">{children}</div>
      </section>
    </SectionContext.Provider>
  );
}

/** A subsection, numbered within its section: 8.1, 8.2… */
export function Subsection({ id, title, children }: { id: string; title: ReactNode; children?: ReactNode }) {
  const sheet = useSheet();
  const section = useSheetSection();
  const n = section && sheet ? sheet.subsection(section.key, id) : null;
  const number = section?.number && n ? `${section.number}.${n}` : null;
  return (
    <>
      <h3 id={id}>{number ? <>{number} · {title}</> : title}</h3>
      {children}
    </>
  );
}

